import { Form, Head } from "@inertiajs/react"
import { Check, Pencil, Skull, X } from "lucide-react"
import { useState } from "react"

import { Divider } from "@/components/divider"
import { Heading } from "@/components/heading"
import { PlayerStatusBadge } from "@/components/player-status-badge"
import { SkillBadge } from "@/components/skill-badge"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import { formatGold, withPlus } from "@/lib/format"
import { getTeamHue } from "@/lib/race-colors"
import { cn } from "@/lib/utils"
import { leaguePath, leagueTeamPath, leagueTeamPlayerPath } from "@/routes"
import type { BreadcrumbItem, PlayerDetailProps } from "@/types"

import { LevelUpPanel } from "./level-up-panel"
import { MatchHistory } from "./match-history"

// ── Sub-components ───────────────────────────────────────────────────────────

interface NumberShieldProps {
  number: number | null
  race: string
  colorHue?: number | null
}

function NumberShield({ number, race, colorHue }: NumberShieldProps) {
  const hue = getTeamHue(race, colorHue)
  return (
    <div
      className="shield flex h-16 w-14 shrink-0 items-start justify-center pt-2"
      style={{
        background: `linear-gradient(180deg, oklch(0.32 0.10 ${hue}), oklch(0.20 0.07 ${hue}))`,
      }}
    >
      <span
        className="font-display text-3xl leading-none font-bold"
        style={{ color: `oklch(0.75 0.14 ${hue})` }}
      >
        #{number ?? "—"}
      </span>
    </div>
  )
}

interface StatBlockProps {
  label: string
  current: number | null
  base: number | null
  isAgPaAv?: boolean
  index: number
}

function StatBlock({
  label,
  current,
  base,
  isAgPaAv = false,
  index,
}: StatBlockProps) {
  const displayValue = isAgPaAv ? withPlus(current) : (current ?? "—")

  const changed = base !== null && current !== null && current !== base
  // For AG/PA/AV, lower number is better
  const actuallyImproved = changed
    ? isAgPaAv
      ? current < base
      : current > base
    : false
  const actuallyDegraded = changed ? !actuallyImproved : false

  const diff = changed ? (isAgPaAv ? base - current : current - base) : 0

  return (
    <div
      className={cn(
        "min-w-14 flex-1 py-3 text-center",
        index > 0 && "border-border border-l",
      )}
    >
      <div
        className={cn(
          "font-display text-[2rem] leading-none font-bold tracking-[0.02em]",
          actuallyImproved &&
            "text-stat-up [text-shadow:0_0_6px_oklch(0.65_0.18_145_/_0.4)]",
          actuallyDegraded &&
            "text-stat-down [text-shadow:0_0_6px_oklch(0.6_0.22_28_/_0.3)]",
        )}
      >
        {displayValue}
        {changed && (
          <span style={{ fontSize: "0.6em", verticalAlign: "super" }}>
            {diff > 0 ? `+${diff}` : `${diff}`}
          </span>
        )}
      </div>
      <div className="text-accent-dim mt-0.5 text-[10px] font-bold tracking-[0.08em] uppercase">
        {label}
      </div>
    </div>
  )
}

// ── Inline Rename ────────────────────────────────────────────────────────────

interface InlineRenameProps {
  leagueId: string
  teamId: string
  playerId: string
  currentName: string
}

function InlineRename({
  leagueId,
  teamId,
  playerId,
  currentName,
}: InlineRenameProps) {
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Rename player"
        onClick={() => setEditing(true)}
        className="text-muted-foreground hover:text-accent ml-2 rounded p-1"
      >
        <Pencil className="size-4" />
      </Button>
    )
  }

  return (
    <Form
      method="patch"
      action={leagueTeamPlayerPath(leagueId, teamId, playerId)}
      className="ml-2 flex items-center gap-2"
      onSuccess={() => setEditing(false)}
    >
      {({ errors, processing }) => (
        <>
          <Input
            name="player[name]"
            defaultValue={currentName}
            className="h-8 w-48"
            autoFocus
            aria-label="Player name"
          />
          <Button
            type="submit"
            size="sm"
            variant="default"
            disabled={processing}
            aria-label="Save name"
          >
            <Check className="size-3" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setEditing(false)}
            aria-label="Cancel rename"
          >
            <X className="size-3" />
          </Button>
          {errors["player.name"] && (
            <p className="text-destructive text-xs">{errors["player.name"]}</p>
          )}
        </>
      )}
    </Form>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function PlayerShow({
  league,
  team,
  player,
  career_stats,
  appearances,
  is_owner,
  advancement_options,
}: PlayerDetailProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
    { title: team.name, href: leagueTeamPath(league.id, team.id) },
    { title: player.name },
  ]

  const isDead = player.status === "dead"
  const showLevelUp =
    player.pending_level_up && !isDead && is_owner && !!advancement_options

  // SPP progress
  const allThresholdsCrossed = player.next_threshold === null
  const prevThreshold = player.prev_threshold
  const progressPercent = allThresholdsCrossed
    ? 100
    : Math.min(
        100,
        ((player.spp_total - prevThreshold) /
          (player.next_threshold! - prevThreshold)) *
          100,
      )

  // SPP breakdown
  const sppBreakdown = [
    {
      label: "Touchdowns",
      count: career_stats.touchdowns,
      spp: career_stats.touchdowns * 3,
    },
    {
      label: "Casualties",
      count: career_stats.casualties_caused,
      spp: career_stats.casualties_caused * 2,
    },
    {
      label: "MVP Awards",
      count: career_stats.mvp_awards,
      spp: career_stats.mvp_awards * 4,
    },
    {
      label: "Completions",
      count: career_stats.completions,
      spp: career_stats.completions * 1,
    },
    {
      label: "Deflections",
      count: career_stats.interceptions,
      spp: career_stats.interceptions * 2,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${player.name} · ${team.name}`} />

      <div className="mx-auto w-full max-w-4xl px-4 py-8">
        {/* ── Fallen in Battle Banner ─────────────────────────────────── */}
        {isDead && (
          <div
            className="mb-6 border-2 p-6 text-center"
            style={{
              background:
                "linear-gradient(180deg, oklch(0.16 0.02 28), oklch(0.12 0.01 28))",
              borderColor: "oklch(0.3 0.08 28)",
              boxShadow: "inset 0 0 20px oklch(0.2 0.06 28 / 0.3)",
            }}
          >
            <Skull
              className="mx-auto mb-2 size-10"
              style={{ color: "oklch(0.45 0.10 28)" }}
            />
            <h2
              className="font-display text-3xl leading-none font-bold"
              style={{
                color: "oklch(0.55 0.12 28)",
                textShadow: "0 0 8px oklch(0.50 0.15 28 / 0.3)",
              }}
            >
              Fallen in Battle
            </h2>
            <p
              className="mt-1 text-sm"
              style={{ color: "oklch(0.45 0.06 28)" }}
            >
              {player.name} has been killed. Nuffle claims another soul.
            </p>
          </div>
        )}

        {/* ── Player content (desaturated when dead) ──────────────────── */}
        <div
          className="space-y-10"
          style={
            isDead ? { filter: "saturate(0.15) brightness(0.7)" } : undefined
          }
        >
          {/* Header */}
          <div className="flex items-start gap-4">
            <NumberShield
              number={player.number}
              race={team.race ?? ""}
              colorHue={team.color_hue}
            />
            <div>
              <div className="mb-1 flex items-center gap-2">
                <h1 className="font-display text-gold hover:border-border-heavy border-b-2 border-dashed border-transparent text-4xl leading-none font-bold transition-[border-color] duration-150">
                  {player.name}
                </h1>
                {is_owner && !isDead && (
                  <InlineRename
                    leagueId={league.id}
                    teamId={team.id}
                    playerId={player.id}
                    currentName={player.name}
                  />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2 py-0.5 text-xs font-bold tracking-widest uppercase"
                  style={{
                    background: `oklch(0.22 0.06 ${getTeamHue(team.race, team.color_hue)} / 0.4)`,
                    color: `oklch(0.75 0.12 ${getTeamHue(team.race, team.color_hue)})`,
                    border: `1px solid oklch(0.35 0.08 ${getTeamHue(team.race, team.color_hue)})`,
                  }}
                >
                  {player.position}
                </span>
                <PlayerStatusBadge
                  status={player.status}
                  injuryDescription={player.injury_description}
                  showHealthy
                />
                {player.journeyman && (
                  <Badge variant="secondary">Journeyman</Badge>
                )}
                <span className="text-sm text-[var(--accent-dim)]">
                  Value: {formatGold(player.cost)} gp
                </span>
              </div>
            </div>
          </div>

          {/* Stat Line */}
          <Card className="flex flex-row gap-0 py-0">
            <CardContent className="flex flex-1 p-0">
              <StatBlock
                label="MA"
                current={player.ma}
                base={player.base_ma}
                index={0}
              />
              <StatBlock
                label="ST"
                current={player.st}
                base={player.base_st}
                index={1}
              />
              <StatBlock
                label="AG"
                current={player.ag}
                base={player.base_ag}
                isAgPaAv
                index={2}
              />
              <StatBlock
                label="PA"
                current={player.pa}
                base={player.base_pa}
                isAgPaAv
                index={3}
              />
              <StatBlock
                label="AV"
                current={player.av}
                base={player.base_av}
                isAgPaAv
                index={4}
              />
            </CardContent>
          </Card>

          {/* Skills + SPP two-column grid */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {/* Skills Panel */}
            <Card className="py-0">
              <CardContent className="pt-5 pb-4">
                <Heading
                  level={3}
                  wrapperClassName="mb-3"
                  className="font-bold"
                >
                  Skills
                </Heading>

                {player.starting_skills.length === 0 &&
                player.earned_skills.length === 0 &&
                player.stat_improvements.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No skills</p>
                ) : (
                  <>
                    {player.starting_skills.length > 0 && (
                      <div className="mb-3">
                        <div
                          className="mb-1.5 text-[10px] font-bold tracking-widest uppercase"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          Starting Skills
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {player.starting_skills.map((skill) => (
                            <SkillBadge
                              key={skill}
                              name={skill}
                              variant="starting"
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {player.earned_skills.length > 0 && (
                      <div className="mb-3">
                        <div
                          className="mb-1.5 text-[10px] font-bold tracking-widest uppercase"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          Earned
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {player.earned_skills.map((skill) => (
                            <SkillBadge
                              key={skill}
                              name={skill}
                              variant="earned"
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {player.stat_improvements.length > 0 && (
                      <>
                        <Divider className="my-3" />
                        <div
                          className="mb-1 text-[10px] font-bold tracking-widest uppercase"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          Stat Increases
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {player.stat_improvements.map((imp) => (
                            <span
                              key={imp}
                              className="text-stat-up text-sm font-semibold [text-shadow:0_0_6px_oklch(0.65_0.18_145_/_0.4)]"
                            >
                              {imp}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* SPP Panel */}
            <Card className="py-0">
              <CardContent className="pt-5 pb-4">
                <Heading
                  level={3}
                  wrapperClassName="mb-3"
                  className="font-bold"
                >
                  Star Player Points
                </Heading>

                <div className="mb-2 flex items-end justify-between">
                  <div>
                    <span className="font-display text-gold text-3xl leading-none font-bold">
                      {player.spp_total}
                    </span>{" "}
                    <span className="text-muted-foreground ml-1 text-sm">
                      SPP
                    </span>
                  </div>
                  <div
                    className="text-xs font-semibold tracking-wide uppercase"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {allThresholdsCrossed ? (
                      "Max level"
                    ) : (
                      <>
                        Next:{" "}
                        <span style={{ color: "var(--accent)" }}>
                          {player.next_threshold} SPP
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="border-border bg-muted mb-1 h-2.5 overflow-hidden border">
                  <div
                    className="h-full transition-[width] duration-300"
                    style={{
                      width: `${progressPercent}%`,
                      background:
                        "linear-gradient(90deg, oklch(0.5 0.2 85), oklch(0.7 0.16 85))",
                    }}
                  />
                </div>
                <div
                  className="mb-1 flex justify-between text-[10px] font-bold tracking-widest uppercase"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  <span>{prevThreshold} (prev)</span>
                  {!allThresholdsCrossed && (
                    <span>{player.next_threshold} (next)</span>
                  )}
                </div>

                <p className="text-muted-foreground mb-3 text-xs">
                  {player.advancements_earned} advancement
                  {player.advancements_earned !== 1 ? "s" : ""} earned ·{" "}
                  {player.advancements_used} used
                </p>

                <Divider className="my-3" />

                <div
                  className="mb-2 text-[10px] font-bold tracking-widest uppercase"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  SPP Breakdown
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {sppBreakdown.map(({ label, count, spp }) => (
                    <div key={label} className="flex justify-between">
                      <span style={{ color: "var(--muted-foreground)" }}>
                        {label}
                      </span>
                      <span className="font-semibold">
                        {count}{" "}
                        <span
                          style={{
                            color: "var(--muted-foreground)",
                            fontWeight: 400,
                          }}
                        >
                          ({spp})
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── Level Up Panel (outside desaturated wrapper) ─────────────── */}
        {showLevelUp && advancement_options && (
          <div className="mt-10">
            <LevelUpPanel
              leagueId={league.id}
              teamId={team.id}
              playerId={player.id}
              playerName={player.name}
              advancementOptions={advancement_options}
              advancementsUsed={player.advancements_used}
              advancementsEarned={player.advancements_earned}
            />
          </div>
        )}

        {/* ── Match History ─────────────────────────────────────────────── */}
        <div className="mt-10">
          <MatchHistory leagueId={league.id} appearances={appearances} />
        </div>
      </div>
    </AppLayout>
  )
}
